import React, { useCallback, useState } from 'react'
import {
  ChefMenuItem,
  MenuItemMod,
  MenuItemModGroup,
  MenuItemModVerb,
} from '@types'
import Checkbox from '@components/common/form/Checkbox'
import Button from '@components/common/form/Button'

interface EditComposedMenuItemProps {
  chefMenuItem: ChefMenuItem
  menuItem: any
  draftComposedAltMenuItem: (
    vendorDescription: string,
    chefMenuItemId: string,
  ) => void
}

type SelectedMod = {
  groupId: string
  verb: MenuItemModVerb
  modName: string
}

const EditComposedMenuItem: React.FC<EditComposedMenuItemProps> = ({
  chefMenuItem,
  menuItem,
  draftComposedAltMenuItem,
}) => {
  const [selected, setSelected] = useState<Record<string, SelectedMod>>({})

  const isSelected = useCallback(
    (modId: string) => Boolean(selected[modId]),
    [selected],
  )

  const createComposedAltItem = () => {
    const verbMap = new Map<string, Set<string>>()
    for (const { verb, modName } of Object.values(selected)) {
      if (!verbMap.has(verb)) {
        verbMap.set(verb, new Set<string>())
      }
      verbMap.get(verb)?.add(modName)
    }
    const parts: string[] = []
    for (const [verb, names] of verbMap) {
      const capVerb = verb[0].toUpperCase() + verb.slice(1)
      parts.push(`${capVerb} ${[...names].join(', ')}`)
    }

    const vendorDescription = parts.join(' - ')
    draftComposedAltMenuItem(vendorDescription, chefMenuItem.id)
  }

  const handleSelectMod = (modGroup: MenuItemModGroup, mod: MenuItemMod) => {
    const next: Record<string, SelectedMod> = { ...selected }
    if (next[mod.id]) {
      delete next[mod.id]
    } else {
      next[mod.id] = {
        groupId: modGroup.id,
        verb: modGroup.verb,
        modName: mod.name,
      }
    }
    setSelected(next)
  }

  const renderModGroup = (modGroup: MenuItemModGroup) => {
    const headerId = `mod-group-${modGroup.id || modGroup.name}`

    return (
      <section
        key={modGroup.id || modGroup.name}
        className="rounded-2xl border bg-white shadow-sm overflow-hidden"
        aria-labelledby={headerId}
        role="group"
      >
        <header className="flex items-center gap-2 px-4 py-3 border-b bg-blue-300">
          <span className="text-larg font-medium uppercase tracking-wide text-gray-900">
            {modGroup.verb}
          </span>
          <h3 id={headerId} className="text-base font-semibold text-gray-900">
            {modGroup.name}
          </h3>
        </header>

        <ul className="divide-y">
          {modGroup.mods.map((mod) => {
            return (
              <li
                key={mod.id}
                className="flex flex-row items-center px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <p className="truncate text-sm text-gray-900">{mod.name}</p>
                <div className="pl-3">
                  <Checkbox
                    checked={isSelected(mod.id)}
                    aria-label={`Select ${mod.name}`}
                    onClick={() => handleSelectMod(modGroup, mod)}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      </section>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-gray-900 my-6 p-4 shadow text-lg">
        Select which modifications from{' '}
        <span className="font-bold">{menuItem.name}</span> you would like to
        make into a composed alternative
      </p>
      {chefMenuItem.availableModGroups.map((mg) => renderModGroup(mg))}
      <div className="flex flex-row items-center">
        <Button
          disabled={Object.keys(selected).length === 0}
          label="Draft Composed Alternative"
          onClick={createComposedAltItem}
        />
      </div>
    </div>
  )
}

export default EditComposedMenuItem
